import { create } from 'zustand';
import { householdService } from '@/services';
import type { GmailConnectionStatus } from '@/hooks/useGmailConnectionStatus';

interface GmailConnectionStoreState {
  // Last household id this data was loaded for. `undefined` = never loaded;
  // `null` = explicitly no household.
  loadedFor: string | null | undefined;
  data: GmailConnectionStatus | undefined;
  isLoading: boolean;
  error: Error | null;
  inflightFor: string | null | undefined;

  /** Load (or reuse) status for the given household. Dedupes. */
  load: (householdId: string | null | undefined) => Promise<void>;

  /** Refetch for the current `loadedFor` household. */
  refetch: () => Promise<void>;
}

const EXPIRY_WARNING_DAYS = 3;
const MS_PER_DAY = 24 * 3600 * 1000;

async function deriveStatus(
  householdId: string | null | undefined,
): Promise<GmailConnectionStatus> {
  if (householdId === null || householdId === undefined) {
    return { state: 'not_connected' };
  }
  const [inbox, sub] = await Promise.all([
    householdService.getInboxAddress(householdId).catch(() => null),
    householdService.getGmailSubscriptionStatus(householdId).catch(() => null),
  ]);
  if (!inbox || !sub) return { state: 'not_connected' };
  // `revoked_at` is set by webhooks-gmail-watch the first time Google
  // refuses our refresh_token with invalid_grant. We check it before
  // `active` because the watch handler ALSO flips active=false on
  // revocation, and the `not_connected` copy ("Connect Gmail once...")
  // doesn't fit a user who already connected and lost permission.
  // gmail-forwarding-setup clears revoked_at on successful re-OAuth.
  if (sub.revoked_at) return { state: 'revoked' };
  if (!sub.active) {
    // Distinguish a soft pause (toggle off; subscription + watch still
    // armed) from a hard disconnect (revoked refresh_token, watch
    // stopped). The disconnect path clears watch_expiry, so re-enabling
    // the toggle wouldn't actually resume processing — surfacing
    // `not_connected` routes the user through full OAuth instead.
    return sub.watch_expiry ? { state: 'paused' } : { state: 'not_connected' };
  }
  // active === true && !revoked below. A recoverable watch-renewal failure
  // (token still valid, only the users.watch re-arm failed) is gated on
  // exactly that, so a genuine hard lapse — which clears watch_expiry AND
  // flips active=false — still falls through to expired/not_connected/revoked
  // above rather than this softer state. Checked before the expiry tests
  // because the renewal-failure path deliberately leaves watch_expiry stale.
  if (sub.watch_renewal_failed_at) return { state: 'needs_attention' };
  if (!sub.watch_expiry) return { state: 'expired' };
  const expiryMs = new Date(sub.watch_expiry).getTime();
  const now = Date.now();
  if (expiryMs < now) return { state: 'expired' };
  const msRemaining = expiryMs - now;
  if (msRemaining < EXPIRY_WARNING_DAYS * MS_PER_DAY) {
    return {
      state: 'expiring_soon',
      daysUntilExpiry: Math.max(1, Math.ceil(msRemaining / MS_PER_DAY)),
    };
  }
  return { state: 'connected' };
}

export const useGmailConnectionStore = create<GmailConnectionStoreState>(
  (set, get) => ({
    loadedFor: undefined,
    data: undefined,
    isLoading: true,
    error: null,
    inflightFor: undefined,

    load: async (householdId) => {
      const { loadedFor, isLoading, inflightFor } = get();
      // Already loaded for this household and not stale → skip.
      if (loadedFor === householdId && !isLoading && get().data !== undefined) {
        return;
      }
      // Already fetching for this household → skip.
      if (inflightFor === householdId && isLoading) return;

      set({
        isLoading: true,
        error: null,
        inflightFor: householdId,
      });
      try {
        const data = await deriveStatus(householdId);
        // Only commit if no newer request superseded ours.
        if (get().inflightFor === householdId) {
          set({
            data,
            loadedFor: householdId,
            isLoading: false,
            inflightFor: null,
          });
        }
      } catch (err) {
        if (get().inflightFor === householdId) {
          set({
            error: err instanceof Error ? err : new Error(String(err)),
            isLoading: false,
            inflightFor: null,
          });
        }
      }
    },

    refetch: async () => {
      const householdId = get().loadedFor;
      set({ isLoading: true, error: null, inflightFor: householdId });
      try {
        const data = await deriveStatus(householdId);
        if (get().inflightFor === householdId) {
          set({ data, isLoading: false, inflightFor: null });
        }
      } catch (err) {
        if (get().inflightFor === householdId) {
          set({
            error: err instanceof Error ? err : new Error(String(err)),
            isLoading: false,
            inflightFor: null,
          });
        }
      }
    },
  }),
);
